import { useEffect, useRef } from "react";
import { Form } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dentallyLogo from "../../../assets/dentally-logo.png";
import PasswordResetInjectors from "./injectors";
import { selectRequestReset } from "./selector";
import { saveRequestReset } from "./slice";
import { stripWhitespace } from "../../../utils/formNormalizers";
import type { ForgotPasswordFormValues } from "./types";
import {
  BackButton,
  FormCard,
  ImageCard,
  LeftCol,
  LogoImage,
  PageRow,
  PrimaryButton,
  RightCol,
  StyledForm,
  StyledInput,
  StyledTitle,
  SubmitFormItem,
  SubtitleText,
} from "./style";

const { Item, useForm } = Form;

export default function ForgotPasswordPage() {
  const [form] = useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { saving, error } = useSelector(selectRequestReset);
  const wasSaving = useRef(false);

  useEffect(() => {
    if (wasSaving.current && !saving && !error) {
      navigate("/verify");
    }
    wasSaving.current = saving;
  }, [saving, error, navigate]);

  function handleFinish(values: unknown) {
    const v = values as ForgotPasswordFormValues;
    dispatch(saveRequestReset({ email: v.email.trim() }));
  }

  return (
    <PageRow>
      <PasswordResetInjectors />
      <LeftCol xs={0} sm={0} md={0} lg={12} xl={12}>
        <ImageCard />
      </LeftCol>

      <RightCol xs={24} sm={24} md={24} lg={12} xl={12}>
        <FormCard vertical align="flex-start">
          <LogoImage
            src={dentallyLogo}
            alt="Dentally"
            width={233}
            preview={false}
          />

          <BackButton
            aria-label="Back to login"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/login")}
          />

          <StyledTitle level={1}>Forgot Password</StyledTitle>

          <SubtitleText>
            A verification code will be sent to your email address
          </SubtitleText>

          <StyledForm
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            requiredMark={false}
          >
            <Item
              name="email"
              label="Email Address"
              normalize={stripWhitespace}
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Enter a valid email" },
              ]}
            >
              <StyledInput
                placeholder="Enter your email address"
                autoComplete="email"
              />
            </Item>

            <SubmitFormItem>
              <PrimaryButton
                type="primary"
                htmlType="submit"
                block
                loading={saving}
              >
                Send Verification Code
              </PrimaryButton>
            </SubmitFormItem>
          </StyledForm>
        </FormCard>
      </RightCol>
    </PageRow>
  );
}
